import { type ImageRulesType } from 'utils/apiList/images'

/**
 * Helper for extract actual client image dimension
 */
export const getImageDimension = (imageUrl: string) =>
  new Promise<{ width: number; height: number }>((resolve, reject) => {
    if (typeof window !== 'undefined') {
      const img = new window.Image()

      img.src = imageUrl

      img.onload = () => {
        const { naturalWidth: width, naturalHeight: height } = img

        resolve({
          width,
          height,
        })
      }

      img.onerror = () => {
        reject(new Error('Failed to get image dimensioon'))
      }
    }
  })

export const getRoundedUpConvertedFileSize = (
  /** size: in bytes */
  size: number,
  convertTo: 'mb' | 'kb' = 'mb',
) => {
  if (convertTo === 'kb') {
    return Math.round(size / 1000)
  }

  return Math.round(size / 1000 / 1000)
}

/**
 * The rules API returns loose MIME values (`image/svg`, `image/jpg`) while the
 * browser reports the canonical ones (`image/svg+xml`, `image/jpeg`), so both
 * sides are folded to the same shape before comparing.
 */
const normalizeImageMimeType = (mimeType: string) =>
  mimeType.toLowerCase().replace(/\+.*$/, '').replace('image/jpeg', 'image/jpg')

const validateImageExtension = (fileType: string, extensions: string[]) =>
  extensions.some(
    (extension) => normalizeImageMimeType(extension) === normalizeImageMimeType(fileType),
  )

const validateImageDimension = (
  imageDimension: { width: number; height: number },
  imageRules: ImageRulesType,
) =>
  imageDimension.width >= imageRules.width.min &&
  imageDimension.width <= imageRules.width.max &&
  imageDimension.height >= imageRules.height.min &&
  imageDimension.height <= imageRules.height.max

export const validateImageByImageRules = (
  imageData: { file: File; imageUrl: string; width: number; height: number },
  imageRules: ImageRulesType,
) => {
  const { file, width, height } = imageData

  if (!(file.size <= imageRules.maxFileSize)) {
    return {
      error: true,
      message: `Maksimum file size ${getRoundedUpConvertedFileSize(imageRules.maxFileSize)}`,
    }
  }

  if (!validateImageExtension(file.type, imageRules.extensions)) {
    return {
      error: true,
      message: 'File type is invalid.',
    }
  }

  if (!validateImageDimension({ width, height }, imageRules)) {
    return {
      error: true,
      message: 'Image dimension is invalid',
    }
  }

  return {
    error: false,
    message: '',
  }
}
